import React, { createContext, useContext, Component } from 'react';
import { connect } from 'react-redux';
import CrownBronze from '../images/CrownBronze.png';
import CrownSilver from '../images/CrownSilver.png';
import CrownGold from '../images/CrownGold.png';
import chessslogo from '../images/chessslogo.png';
import PropTypes from 'prop-types';
import { firestore } from '../firebase';
import { collection, query, where, getDocs, limit } from "firebase/firestore";

// Create context for Premium status
const PremiumContext = createContext();

// Premium tier levels
export const PREMIUM_TIERS = {
  NONE: 0,    // Non-premium
  BRONZE: 1,  // Bronze tier
  SILVER: 2,  // Silver tier
  GOLD: 3     // Gold tier
};

// Convert PremiumProvider to class component with connect
class PremiumProviderComponent extends Component {
  // Cache for user data to avoid repeated Firestore queries
  userCache = {};

  render() {
    const { credentials, authenticated, children } = this.props;
    
    // Debug log to see what credentials we're getting from Redux
    
    
    // Determine the premium tier based on Redux state
    let currentPremiumTier = PREMIUM_TIERS.NONE; // Default to NONE

    if (authenticated && credentials) {
      // Handle older users who might not have a premium field by defaulting to 0
      const userPremiumValue = credentials.premium !== undefined ? credentials.premium : PREMIUM_TIERS.NONE;
      
      // Ensure the premium value is valid (0, 1, 2, 3)
      if (userPremiumValue >= PREMIUM_TIERS.NONE && userPremiumValue <= PREMIUM_TIERS.GOLD) {
        currentPremiumTier = userPremiumValue;
      } else {
        console.warn(`Invalid premium value received from user data: ${userPremiumValue}. Defaulting to NONE.`);
      }
    }

    // Log the final determined tier
    

    // Derived state based on the user's tier
    const isPremiumUser = currentPremiumTier > PREMIUM_TIERS.NONE;

    // Premium features configuration object - based on the current user's premium status
    const premiumFeatures = {
      // Hide ads/premium upsells for premium users (true = hide, false = show)
      hideAdsAndUpsells: isPremiumUser,

      // Stockfish access on game viewing pages
      stockfishAccess: isPremiumUser,

      // Example of other premium features that could be added:
      // enhancedVisibility: isPremiumUser,
      // videoExportCapability: isPremiumUser,
    };

    return (
      <PremiumContext.Provider value={{
        isPremiumUser,
        premiumTier: currentPremiumTier,
        renderUserWithBadge: this.renderUserWithBadge,
        premiumFeatures,
        PREMIUM_TIERS
      }}>
        {children}
      </PremiumContext.Provider>
    );
  }

  // Method to fetch user data from Firestore
  getUserData = async (username) => {
    try {
      // Check if we have this user in cache first
      if (this.userCache[username]) {
        return this.userCache[username];
      }

      // Clean the username (remove @ if present)
      const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
      
      // Query Firestore for the user
      const userQuery = query(
        collection(firestore, "users"), 
        where("handle", "==", cleanUsername),
        limit(1)
      );
      
      const querySnapshot = await getDocs(userQuery);
      
      if (!querySnapshot.empty) {
        // We found the user
        const userData = querySnapshot.docs[0].data();
        // Store in cache for future use
        this.userCache[username] = userData;
        return userData;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  }

  // Enhanced function that handles multiple parameter formats
  renderUserWithBadge = (userOrUsername, premiumParam = null, showPhoto = true, photoUrlParam = null) => {
    let username;
    let premium = PREMIUM_TIERS.NONE;
    let photoUrl = null;

    // Case 1: userOrUsername is an object with handle/premium properties
    if (userOrUsername && typeof userOrUsername === 'object') {
      username = userOrUsername.handle || userOrUsername.userHandle;
      premium = userOrUsername.premium !== undefined ? userOrUsername.premium : PREMIUM_TIERS.NONE;
      
      // Access the photoUrl property directly from the user object
      photoUrl = userOrUsername.photoUrl || null;
      
      // Debug the photoUrl we're getting
      
      
      // We no longer filter out pendingProfilePictures as the actual valid photos may still use this path
      // Only filter out if explicitly marked as pending
      if (userOrUsername.pendingPhotoUrl === photoUrl) {
        photoUrl = null;
      }
    } 
    // Case 2: userOrUsername is a string (username) and premium is passed as second param
    else if (typeof userOrUsername === 'string') {
      username = userOrUsername;
      if (premiumParam !== null && premiumParam !== undefined) {
        premium = premiumParam;
      }
      
      // First check if photoUrlParam was provided
      if (photoUrlParam) {
        photoUrl = photoUrlParam;
      } 
      // Next, try to find user in Redux state users
      else if (this.props.users && this.props.users[username]) {
        photoUrl = this.props.users[username].photoUrl || null;
      }
      // Next, try to match the current user if available
      else if (this.props.credentials && 
          (this.props.credentials.handle === username || 
           this.props.credentials.userHandle === username)) {
        photoUrl = this.props.credentials.photoUrl || null;
      }
      // If we still don't have photoUrl, and we're rendering immediately (no async)
      // then as a fallback we'll render the default logo and try to fetch the user data
      // for next time
      else {
        // Try to fetch the user data for next render
        this.getUserData(username).then(userData => {
          if (userData && userData.photoUrl) {
            // Update cache for next render
            this.userCache[username] = userData;
          }
        });
      }
      
      
    }
    // Handle invalid input
    else {
      // console.warn('renderUserWithBadge called with invalid parameters:', userOrUsername, premiumParam);
      return null;
    }

    if (!username) return null;

    // Remove @ if it already exists at the beginning of the username
    const cleanUsername = username.startsWith('@') ? username.substring(1) : username;
    
    // Handle older users who might not have a premium field
    premium = premium !== undefined ? premium : PREMIUM_TIERS.NONE;

    let crownImg = null;
    let crownAlt = '';

    switch (premium) {
      case PREMIUM_TIERS.BRONZE:
        crownImg = CrownBronze;
        crownAlt = 'Bronze';
        break;
      case PREMIUM_TIERS.SILVER:
        crownImg = CrownSilver;
        crownAlt = 'Silver';
        break;
      case PREMIUM_TIERS.GOLD:
        crownImg = CrownGold;
        crownAlt = 'Gold';
        break;
      default:
        // No crown for non-premium users, but still include the @ symbol
        return (
          <span className="user-with-badge" style={{ color: 'gold' }}>
            {showPhoto && photoUrl ? (
              <img 
                src={photoUrl} 
                alt={`${cleanUsername}'s profile`} 
                className="user-badge-photo" 
                style={{ height: '25px', width: '25px', borderRadius: '50%', marginRight: '5px', verticalAlign: 'middle', border: '1px solid gold' }}
              />
            ) : showPhoto ? (
              <img 
                src={chessslogo} 
                alt="Default profile" 
                className="user-badge-photo" 
                style={{ height: '25px', width: '25px', borderRadius: '50%', marginRight: '5px', verticalAlign: 'middle', border: '1px solid gold' }}
              />
            ) : null}
            @{cleanUsername}
          </span>
        );
    }

    return (
      <span className="user-with-badge" style={{ color: premium === PREMIUM_TIERS.GOLD ? '#FFD700' : premium === PREMIUM_TIERS.SILVER ? '#C0C0C0' : premium === PREMIUM_TIERS.BRONZE ? '#CD7F32' : 'gold' }}>
        {showPhoto && photoUrl ? (
          <img 
            src={photoUrl} 
            alt={`${cleanUsername}'s profile`} 
            className="user-badge-photo" 
            style={{ height: '25px', width: '25px', borderRadius: '50%', marginRight: '5px', verticalAlign: 'middle', border: '1px solid gold' }}
          />
        ) : showPhoto ? (
          <img 
            src={chessslogo} 
            alt="Default profile" 
            className="user-badge-photo" 
            style={{ height: '25px', width: '25px', borderRadius: '50%', marginRight: '5px', verticalAlign: 'middle', border: '1px solid gold' }}
          />
        ) : null}
        @{cleanUsername}
        <img
          src={crownImg}
          alt={`${crownAlt} Crown`}
          className="premium-badge"
          style={{ height: '30px', marginLeft: '2px', verticalAlign: 'middle' }}
        />
      </span>
    );
  };
}

// Set up prop types for the component
PremiumProviderComponent.propTypes = {
  credentials: PropTypes.object,
  authenticated: PropTypes.bool,
  children: PropTypes.node.isRequired,
  users: PropTypes.object
};

// Map Redux state to component props
const mapStateToProps = (state) => ({
  credentials: state.user.credentials,
  authenticated: state.user.authenticated,
  users: state.data.users // Add users from state if available
});

// Create the connected component
export const PremiumProvider = connect(mapStateToProps)(PremiumProviderComponent);

// Custom hook to use premium context
export const usePremium = () => {
  const context = useContext(PremiumContext);
  if (context === undefined) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
};

export default PremiumContext; 